import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRight, ChevronLeft, AlertCircle, Mail } from 'lucide-react';

import Header from '../components/Header';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorDisplay from '../components/ErrorDisplay';
import SuccessMessage from '../components/SuccessMessage';
import QuestionCard, { Question, ScaleOption } from '../components/QuestionCard';
import { N8N_BASE_URL, API_ENDPOINTS } from '../utils/constants';
import { translateError } from '../utils/translations';

interface SurveyData {
    survey_name: string;
    questions: Question[];
    answer_scale: Record<string, ScaleOption[]>;
    send_report_to_email?: boolean;
}

const QUESTIONS_PER_PAGE = 10;

const SessionPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [completed, setCompleted] = useState(false);

    const [data, setData] = useState<SurveyData | null>(null);
    const [answers, setAnswers] = useState<Record<string, number>>(() => {
        try {
            if (token) {
                const saved = localStorage.getItem(`survey_answers_${token}`);
                return saved ? JSON.parse(saved) : {};
            }
        } catch (e) {
            console.error("Error loading answers from localStorage", e);
        }
        return {};
    });

    const [currentPageIndex, setCurrentPageIndex] = useState<number>(() => {
        try {
            if (token) {
                const saved = localStorage.getItem(`survey_page_index_${token}`);
                return saved ? parseInt(saved, 10) : 0;
            }
        } catch (e) {
            console.error("Error loading page index from localStorage", e);
        }
        return 0;
    });

    const scrollToTop = useCallback(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        const root = document.getElementById('root');
        if (root) {
            root.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
    }, []);

    useEffect(() => {
        const fetchSurvey = async () => {
            if (!token) {
                setError('מזהה השאלון אינו תקין או שאינו קיים במערכת.');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`${N8N_BASE_URL}${API_ENDPOINTS.GET_SURVEY}?token=${token}`);

                const text = await response.text();
                let json: any = null;
                try {
                    if (text) json = JSON.parse(text);
                } catch (e) {
                    console.error("Failed to parse session JSON:", text);
                }

                if (!response.ok || !json || json.error) {
                    const msg = translateError(json?.error_description || 'מזהה השאלון אינו תקין או שאינו קיים במערכת.');
                    throw new Error(msg || 'Error');
                }

                setData(json);
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchSurvey();
    }, [token]);

    useEffect(() => {
        if (data?.survey_name) {
            document.title = data.survey_name;
        }
    }, [data]);

    useEffect(() => {
        if (token) {
            localStorage.setItem(`survey_page_index_${token}`, currentPageIndex.toString());
        }
        scrollToTop();
    }, [currentPageIndex, token, scrollToTop]);

    const handleAnswer = (qId: string, val: number) => {
        const newAnswers = { ...answers, [qId]: val };
        setAnswers(newAnswers);
        if (token) {
            localStorage.setItem(`survey_answers_${token}`, JSON.stringify(newAnswers));
        }
    };

    const handleReset = () => {
        if (window.confirm('האם את/ה בטוח/ה שברצונך לאפס את השאלון? כל התשובות וההתקדמות יימחקו.')) {
            setAnswers({});
            if (token) {
                localStorage.removeItem(`survey_answers_${token}`);
                localStorage.removeItem(`survey_page_index_${token}`);
            }
            setCurrentPageIndex(0);
        }
    };

    const handleSubmit = async () => {
        if (!token) return;

        const payload = {
            answers: Object.entries(answers).map(([key, value]) => ({
                question_id: key,
                answer_value: value
            }))
        };

        try {
            setSubmitting(true);
            const response = await fetch(`${N8N_BASE_URL}${API_ENDPOINTS.SUBMIT_ANSWERS}?token=${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const json = await response.json();
                const msg = translateError(json?.error_description || 'שגיאה בשליחת התשובות.');
                throw new Error(msg || 'Error');
            }

            // Success
            localStorage.removeItem(`survey_answers_${token}`);
            localStorage.removeItem(`survey_page_index_${token}`);
            setCompleted(true);
        } catch (err: any) {
            setSubmitError(err.message);
            setTimeout(() => setSubmitError(null), 10000);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-white flex flex-col"><Header /><main className="flex-grow flex flex-col items-center pt-20"><LoadingSpinner message="טוען שאלון..." /></main></div>;
    if (submitting) return <div className="min-h-screen bg-white flex flex-col"><Header /><main className="flex-grow flex flex-col items-center pt-20"><LoadingSpinner message="שומר תשובות..." /></main></div>;
    if (error) return <ErrorDisplay message={error} />;
    if (completed) return (
        <SuccessMessage
            extraContent={
                data?.send_report_to_email ? (
                    <div className="bg-brand-gold/10 p-6 rounded-2xl border border-brand-gold/20 mb-8">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Mail className="w-5 h-5 text-brand-gold" />
                            <p className="text-brand-dark font-medium">דוח אישי יישלח אליך במייל</p>
                        </div>
                        <p className="text-sm text-brand-dark/60">
                            בדקות הקרובות תקבל/י לתיבת המייל דוח אישי מפורט בפורמט PDF.
                        </p>
                    </div>
                ) : undefined
            }
        />
    );

    if (!data || !data.questions.length) return <ErrorDisplay message="לא נמצאו שאלות בטופס זה." />;

    const sortedQuestions = Array.isArray(data?.questions)
        ? [...data.questions].sort((a: any, b: any) => (a.order_index || 0) - (b.order_index || 0))
        : [];
    const totalPages = Math.ceil(sortedQuestions.length / QUESTIONS_PER_PAGE);
    const currentPageQuestions = sortedQuestions.slice(
        currentPageIndex * QUESTIONS_PER_PAGE,
        (currentPageIndex + 1) * QUESTIONS_PER_PAGE
    );

    const allAnsweredOnPage = currentPageQuestions.every(q => answers[q.id] !== undefined);
    const totalQuestions = sortedQuestions.length;
    const totalAnswered = Object.keys(answers).length;
    const isLastPage = currentPageIndex === totalPages - 1;

    return (
        <div className="relative min-h-screen bg-white">
            {submitError && (
                <div className="fixed top-4 left-4 right-4 z-[9999] animate-in fade-in slide-in-from-top-4 duration-300 px-4">
                    <div className="max-w-md mx-auto bg-red-50 border border-red-100 p-4 rounded-2xl shadow-xl text-red-800 text-sm flex flex-col items-center gap-2 text-center relative">
                        <button
                            onClick={() => setSubmitError(null)}
                            className="absolute top-2 left-2 text-red-300 hover:text-red-500 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <div className="w-full">{submitError}</div>
                    </div>
                </div>
            )}

            <div className="flex flex-col pb-32">
                <Header
                    showNav={true}
                    pageIndex={currentPageIndex}
                    totalPages={totalPages}
                    onReset={handleReset}
                />

                <div className="max-w-3xl mx-auto px-4 relative z-20 w-full">
                    {/* Progress Info */}
                    <div className="mb-10 text-center pt-8">
                        <div className="w-full h-1 bg-gray-100 rounded-full mb-3">
                            <div
                                className="h-full bg-brand-gold rounded-full transition-all duration-500 shadow-[0_0_10px_rgba(186,163,126,0.3)]"
                                style={{ width: `${((currentPageIndex + 1) / totalPages) * 100}%` }}
                            />
                        </div>
                        <div className="flex justify-between text-xs text-brand-dark opacity-60 font-medium">
                            <span>{Math.round((totalAnswered / totalQuestions) * 100)}% הושלמו</span>
                            <span>נותרו: {totalQuestions - totalAnswered} שאלות</span>
                        </div>
                    </div>

                    {/* Questions */}
                    <div className="space-y-6">
                        {currentPageQuestions.map(q => (
                            <QuestionCard
                                key={q.id}
                                question={q}
                                value={answers[q.id]}
                                onChange={(val) => handleAnswer(q.id, val)}
                                answerScales={data.answer_scale || {}}
                            />
                        ))}
                    </div>
                </div>

                {/* Navigation Footer */}
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] z-50">
                    <div className="max-w-3xl mx-auto flex justify-between items-center">
                        <button
                            onClick={() => setCurrentPageIndex(p => p - 1)}
                            disabled={currentPageIndex === 0}
                            className={`flex items-center px-4 sm:px-6 py-3 rounded-full font-medium transition-colors ${currentPageIndex === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-brand-dark hover:bg-gray-50'
                                }`}
                        >
                            <ChevronRight className="w-5 h-5 ml-1" />
                            <span className="hidden sm:inline">הקודם</span>
                            <span className="sm:hidden">חזור</span>
                        </button>

                        <button
                            onClick={isLastPage ? handleSubmit : () => setCurrentPageIndex(p => p + 1)}
                            disabled={!allAnsweredOnPage}
                            className={`flex items-center px-6 sm:px-8 py-3 rounded-full font-bold shadow-md transition-all transform active:scale-95 ${allAnsweredOnPage
                                ? 'bg-brand-dark text-white hover:bg-brand-dark/90 hover:shadow-lg hover:-translate-y-0.5'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                        >
                            {isLastPage ? 'סיום ושליחה' : 'הבא'}
                            {!isLastPage && <ChevronLeft className="w-5 h-5 mr-1" />}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SessionPage;
